// Main-thread entry: load the wasm module, build the two control modes (staged
// bases builder + standalone movement tray), render with wgpu, and drive mesh
// generation through a worker. Also wires the top bar, the cart page, and the
// hash route between them.
//
// The wasm module is kept EXTERNAL to the bundle (see the flake's esbuild
// step); wasm-bindgen's init() locates viewer_bg.wasm relative to
// import.meta.url, so "../pkg/viewer.js" must resolve at runtime.
import init from "../pkg/viewer.js";
import { Form } from "./form.js";
import { BuildState } from "./build.js";
import { parseScene } from "./scene.js";
import { Viewer3d } from "./viewer3d.js";
import { wireViewerDock } from "./viewer-dock.js";
import { WorkerClient } from "./worker-client.js";
import { Cart } from "./cart.js";
import { wireImportPanel } from "./import-panel.js";

const canvas = document.getElementById("canvas") as HTMLCanvasElement;
const overlay = document.getElementById("overlay") as HTMLElement;
const overlayText = document.getElementById("overlay-text") as HTMLElement;
const statusDiv = document.getElementById("status") as HTMLElement;

// --- Overlay / spinner ---------------------------------------------------

let overlayTimer: ReturnType<typeof setTimeout> | null = null;

function showOverlay(text: string) {
  if (overlayTimer !== null) clearTimeout(overlayTimer);
  overlayTimer = null;
  overlayText.textContent = text;
  overlay.classList.remove("hidden", "error");
}

// Show the spinner only if the work is still going after a grace period, so
// fast rebuilds don't flash it.
function showOverlaySoon(text: string, delay = 300) {
  if (overlayTimer !== null) clearTimeout(overlayTimer);
  overlayTimer = setTimeout(() => showOverlay(text), delay);
}

function hideOverlay() {
  if (overlayTimer !== null) clearTimeout(overlayTimer);
  overlayTimer = null;
  overlay.classList.add("hidden");
}

function showError(text: string) {
  showOverlay(text);
  overlay.classList.add("error");
}

// --- Routing --------------------------------------------------------------

const editorEl = document.getElementById("editor") as HTMLElement;
const cartPageEl = document.getElementById("cart-page") as HTMLElement;

// Two routes: the editor, and "#cart". Everything stays mounted (the renderer
// and the worker keep running), so a route change only swaps visibility.
function showRoute() {
  const wasOnCart = !cartPageEl.hidden;
  const onCart = location.hash === "#cart";
  editorEl.hidden = onCart;
  cartPageEl.hidden = !onCart;
  // The route decides how much room a focused control needs above it (the
  // editor route docks the viewer under the bar on mobile).
  document.documentElement.classList.toggle("cart-route", onCart);
  // A route arrives at its top rather than inheriting the other one's offset
  // (the page scrolls on mobile; the cart page scrolls itself on desktop).
  if (onCart !== wasOnCart) {
    window.scrollTo(0, 0);
    cartPageEl.scrollTop = 0;
  }
  // Navigating away dismisses a stale error; only a fresh mesh would otherwise
  // clear one, and until then it covers the model it is no longer about.
  if (overlay.classList.contains("error")) hideOverlay();
}

// Replace rather than push, so Back leaves the app instead of bouncing between
// the routes, and skip the redundant hashchange that would follow.
function showEditor() {
  if (location.hash) {
    history.replaceState(null, "", location.pathname + location.search);
  }
  showRoute();
}

window.addEventListener("hashchange", showRoute);
// Before the wasm boot below, so a reload on "#cart" opens on the cart page
// rather than showing the editor for as long as the load takes.
showRoute();

// The app name is the way back to the editor. It routes through showEditor
// rather than through its own href, so the trip leaves no history entry.
const appNameLink = document.querySelector(".app-name") as HTMLAnchorElement;
appNameLink.addEventListener("click", (e) => {
  e.preventDefault();
  showEditor();
});

// --- Boot ----------------------------------------------------------------

showOverlay("Loading viewer…");
await init();

const viewer3d = await Viewer3d.create(canvas);
wireViewerDock(() => !editorEl.hidden);

// The active control mode drives the scene the worker builds. Both build
// forms stay mounted; switching modes only toggles which panel is visible.
type Mode = "bases" | "trays";
let mode: Mode = "bases";

const build = new BuildState(
  {
    masterSize: document.getElementById("master-size") as HTMLElement,
    baseChoice: document.getElementById("base-choice") as HTMLSelectElement,
    baseParams: document.getElementById("base-params") as HTMLElement,
    topperChoice: document.getElementById("topper-choice") as HTMLSelectElement,
    topperParams: document.getElementById("topper-params") as HTMLElement,
    bitChoice: document.getElementById("bit-choice") as HTMLSelectElement,
    addBit: document.getElementById("add-bit") as HTMLButtonElement,
    bitList: document.getElementById("bit-list") as HTMLElement,
  },
  { requestMesh: (refit) => worker.requestMesh(refit) },
);

// The tray mode reuses the standalone Form, pinned to the movement tray (its
// archetype select is hidden in this mode).
const trayForm = new Form(
  document.getElementById("tray-params") as HTMLElement,
  { requestMesh: (refit) => worker.requestMesh(refit) },
);

// The scene the worker (and cart) builds depends on the active mode.
function sceneJson(): string {
  return mode === "bases" ? build.sceneJson() : trayForm.sceneJson();
}

const worker = new WorkerClient(sceneJson, {
  onMesh: ({ vertices, ms, refit, moreQueued }) => {
    viewer3d.setMesh(vertices, refit);
    const tris = (vertices.length / 6 / 3).toLocaleString();
    statusDiv.textContent = `${tris} triangles in ${ms.toFixed(0)} ms`;
    // Keep the spinner up across a coalesced burst (a follow-up rebuild is
    // about to run); hide only when the queue is going idle.
    if (!moreQueued) hideOverlay();
  },
  onMeshError: (message) => {
    showError(`Mesh generation crashed: ${message}`);
    statusDiv.textContent = "";
  },
  onMeshRequested: () => showOverlaySoon("Building mesh…"),
});

// --- Mode toggle + stepper -----------------------------------------------

const basesPanel = document.getElementById("bases-mode") as HTMLElement;
const traysPanel = document.getElementById("trays-mode") as HTMLElement;

// Show a mode's panel. No rebuild: the callers decide, since restoring a scene
// wants a single request once its forms are back.
function applyMode(next: Mode) {
  mode = next;
  basesPanel.hidden = next !== "bases";
  traysPanel.hidden = next !== "trays";
  for (const btn of document.querySelectorAll<HTMLButtonElement>(".mode-btn")) {
    btn.classList.toggle("active", btn.dataset.mode === next);
  }
}

function setMode(next: Mode) {
  applyMode(next);
  // A whole different model: reframe.
  worker.requestMesh(true);
}

for (const btn of document.querySelectorAll<HTMLButtonElement>(".mode-btn")) {
  btn.addEventListener("click", () => setMode(btn.dataset.mode as Mode));
}

// Stepper: headers are always visible and clickable; all step bodies stay
// mounted (state never lost) and only the active one is shown.
const stepHeaders = [...document.querySelectorAll<HTMLElement>(".step-header")];
const stepBodies = [...document.querySelectorAll<HTMLElement>(".step-body")];

let currentStep = 0;
function showStep(index: number) {
  currentStep = index;
  stepHeaders.forEach((h, i) => h.classList.toggle("active", i === index));
  stepBodies.forEach((b, i) => (b.hidden = i !== index));
}
stepHeaders.forEach((h, i) => h.addEventListener("click", () => showStep(i)));
showStep(0);

// Load a stored or shared scene into the mode that owns it and show the editor.
// Returns a message when the scene can't be loaded, so the caller can report it
// wherever the user asked for the load: a scene the validation rejects never
// reaches a form, and neither the route nor the mode moves.
function loadScene(json: string): string | null {
  const scene = parseScene(json);
  if (scene == null) return "not a scene this build can load";
  try {
    if (scene.kind === "build") build.loadScene(scene);
    else trayForm.loadScene(scene);
  } catch (err) {
    return (err as Error).message;
  }
  // The forms hold the scene; show it. The editor comes back before the rebuild
  // because the canvas has no size while the cart page is up.
  applyMode(scene.kind === "build" ? "bases" : "trays");
  showEditor();
  // A whole different model: reframe.
  worker.requestMesh(true);
  return null;
}

// The top bar's share importer, reachable from either route.
wireImportPanel(
  {
    toggle: document.getElementById("import-toggle") as HTMLButtonElement,
    panel: document.getElementById("import-panel") as HTMLElement,
    input: document.getElementById("import-input") as HTMLInputElement,
    loadButton: document.getElementById("import-load") as HTMLButtonElement,
    errorEl: document.getElementById("import-error") as HTMLElement,
  },
  loadScene,
);

// Randomise the currently-visible bases step's form (base or topper); other
// steps have no randomisable archetype form, so it's a no-op there.
const randomiseButton = document.getElementById("randomise") as HTMLButtonElement;
randomiseButton.addEventListener("click", () => {
  if (mode === "trays") {
    trayForm.randomise();
    return;
  }
  build.randomiseStep(currentStep);
});

// --- Cart ----------------------------------------------------------------

// The active mode supplies the scene and label an add stores. How it prints is
// the cart's business, not the editor's.
function cartSource() {
  return mode === "bases"
    ? { sceneJson: build.sceneJson(), label: build.label() }
    : { sceneJson: trayForm.sceneJson(), label: "Movement tray" };
}

const cart = new Cart(
  worker,
  cartSource,
  {
    quantityInput: document.getElementById("cart-quantity") as HTMLInputElement,
    addButton: document.getElementById("cart-add") as HTMLButtonElement,
    badgeEl: document.getElementById("cart-count") as HTMLElement,
    listEl: document.getElementById("cart-list") as HTMLElement,
    summaryEl: document.getElementById("cart-summary") as HTMLElement,
    downloadButton: document.getElementById("cart-download") as HTMLButtonElement,
    clearButton: document.getElementById("cart-clear") as HTMLButtonElement,
    exportFormatSelect: document.getElementById("export-format") as HTMLSelectElement,
    exportModeSelect: document.getElementById("export-mode") as HTMLSelectElement,
    glueClearanceInput: document.getElementById("glue-clearance") as HTMLInputElement,
  },
  {
    // The overlay lives over the viewer, so an error raised from the cart page
    // needs the editor back in view to be seen.
    onError: (message) => {
      showEditor();
      showError(message);
    },
    onLoadScene: (json) => loadScene(json),
    // The item's picture is the view it was added from.
    captureThumbnail: () => viewer3d.captureThumbnail(),
    // The print settings describe a build; a standalone piece (a movement tray)
    // is one solid with nothing to glue.
    exportOptions: (json, settings) =>
      parseScene(json)?.kind === "build"
        ? settings
        : { mode: "union-all", clearance: 0 },
  },
);
// Initial load frames the default build; the stored cart loads alongside it.
// Neither waits on the other: reading storage is slow at best and can fail
// outright, which the cart reports itself rather than hold up the viewer.
worker.requestMesh(true);
void cart.init();
